import type { User } from "@/services/user/userTypes";
import { buildMode, BuildMode } from "@/services/buildMode";

enum EventName {
  Messaging_IWantToTalkClick = "mkt_messages-funnel-click",
  Messaging_ConversationStarted = "mkt_messages-funnel-first-message",
  Messaging_FirstReaction = "mkt_messages-funnel-first-reaction",
  AiIdentification_Completed = "dev_ai_vision_identification_completed",
  AiIdentification_Successful = "dev_ai_identification_successful",
  AiIdentification_Unsuccessful = "dev_ai_identification_unsuccessful",
  AiIdentification_ItemCreated = "dev_ai_vision_item_created",
  AiIdentification_LimitedPopupShown = "mkt_ai_vision_limited_popup_shown",
  AiIdentification_SaveIntoCollectionClick = "mkt_ai_vision_save_into_collection_click",
  AiIdentification_NextIdentificationClick = "mkt_ai_vision_next_identification_click",
  AiIdentification_UnauthorizedError = "mkt_ai_vision_unauthorized_error",
  AiIdentification_UploadImageBtn_DeadClick = "dev_ai_identification_nahrajFotografii_deadclick",
  AiIdentification_IdentifyBtn_DeadClick = "dev_ai_identification_identifikujKartu_deadclick",
  Collection_AddItemBtn_Click = "dev_addItem_collection_click",
  Registration_Done = "registration_done",
  Registration_FromBestDeals = "dev_registration_from_bestdeals",
  Cart_AddItem_FromBestDeals = "dev_cart_additem_from_bestdeals",
  ShowPricesSettingsChange = "dev_user_show_prices",
  EmailNotVerifiedDialog = "dev_registration_verify_email",
  Marketing_ImagoBanner_Click = "mkt_imago_banner_click",
  Menu_AiIdentificationCta_Click = "mkt_menu_aiIdentification_click",
  Menu_AddItemCta_Click = "mkt_menu_add-Item_click",
  Marketplace_Banner_Click = "dev_marketplace_banner_type2",
  Marketplace_CartDialogContinue_Click = "mkt_basketDialog_continue",
  Marketplace_CartDialogGoToBasket_Click = "mkt_basketDialog_goToBasket",
  Marketplace_DisabledPayByCard_Click = "mkt_basket_step2_payByCardDisabled",
  Marketplace_SecuredPaymentDialogLink1_Click = "mkt_basket_step2_savePaymentLink_DialogShown",
  Marketplace_SecuredPaymentDialogLink2_Click = "mkt_basket_step2_savePaymentLink2_DialogShown",
  Marketplace_ToCartBtn_User_Click = "mkt_marketplace_detail_toBasket_click",
  Marketplace_OfferPriceOpenDialog_Click = "dev_offerPrice_step1",
  Marketplace_SubmitOfferPrice_Click = "dev_offerPrice_step2",
  Marketplace_OfferedPriceRatio_Data = "dev_offerPrice_ratio",
  Marketplace_ListItem_CreatedViaAddItemDialog = "dev_marketplace_listItem_via_addItem",
  Marketplace_SellItemBtn_Click = "dev_sellItem_marketplace_click",
  Messages_ContactsForbiddenDialog_Show = "dev_messages_dialogWarningNotSharingContact_show",
  Topbar_CartBtn_Click = "mkt_topbar_cart_click",
  Topbar_MenuBtn_Click = "mkt_topbar_menu_click",
  AddItem_ToCollection_Click = "mkt_addItemDialog_toCollectio_click",
  AddItem_ToMarketplace_Click = "mkt_addItemDialog_toMarketplace_click",
  Gamification_Profile_Click = "dev_gamification_click_userProfile",
  Gamification_UserMenu_Click = "dev_gamification_click_userMenu",
  Dashboard_HodnotaPanel_DeadClick = "dev_dashboard_hodnotaPanel_deadclick",
  Dashboard_TrzisteTab_Click = "dev_dashboard_trziste_tab_click",
  Dashboard_TrzisteContent_Click = "dev_dashboard_trziste_content_click",
  Dashboard_LastItemsContent_Click = "dev_dashboard_lastItems_content_click",
  Like_Click = "dev_like_click",
  Checkout_AuthCta_Clicked = "checkout_auth_cta_clicked",
  Checkout_AuthCompleted = "checkout_auth_completed",
  SessionReplay_UnloggedSellerPage = "record-unlogged-seller-page",
  Marketplace_Negotiation_CtaClick = "negotiation_ctaClick",
  Marketplace_Negotiation_DialogOpen = "negotiation_dialogOpen",
  Marketplace_Negotiation_OfferSubmit = "negotiation_offerSubmit",
  Marketplace_Negotiation_SuggestionClick = "negotiation_suggestionClick",
  Marketplace_Negotiation_Respond = "negotiation_respond",
  Marketplace_Negotiation_AddedToBasket = "negotiation_addedToBasket",
  Marketplace_Negotiation_ListView = "negotiation_listView",
  Marketplace_Negotiation_HistoryExpand = "negotiation_historyExpand",
  Marketplace_Order_Completed = "checkout_orderCompleted",
  MyItems_BulkSelection_Start = "dev_myItems_bulkSelection_start",
  MyItems_BulkAction_Click = "dev_myItems_bulkAction_click",
  MyItems_BulkAction_Completed = "dev_myItems_bulkAction_completed",
  BatchAiIdentification_AddPhotosBtn_Click = "dev_batchAiIdentification_addPhotosBtn_click",
  AiIdentification_AddToCollectionFailed = "aiIdentification_addToCollectionFailed",
  Database_ItemCreated = "database_itemCreated",
  CuratedList_MenuClick = "curatedList_menuClick",
  CuratedList_ShareClick = "curatedList_shareClick",
  CuratedList_BuyMissingClick = "curatedList_buyMissingClick",
  CuratedList_FillPositionClick = "curatedList_fillPositionClick",
  CuratedList_MasterSetEdiceClick = "curatedList_masterSetEdiceClick",
  CuratedList_AddToMasterSetClick = "curatedList_addToMasterSetClick",
  HelpCenter_Search = "dev_helpCenter_search",
  MyItems_BulkRenewPartial_Shown = "myItems_bulkRenewPartialShown",
  Checkout_MoreFromSeller_Click = "checkout_moreFromSellerClick",
  AutoExtend_BulkRenewOfferDialog_Shown = "autoExtend_bulkRenewOfferDialogShown",
  AutoExtend_BulkRenewOfferDialog_EnableClick = "autoExtend_bulkRenewOfferDialogEnableClick",
  HelpCenter_ContextualLink_Click = "helpCenter_contextualLinkClick",
  Marketplace_EmptySearchResult_Exit = "marketplace_emptySearchResult_exit",
  /** A marketplace quick-filter tile (Sport on sports cards, Edice on Pokémon) was picked — `name` carries the tile's own label, e.g. "Hokej". Clearing the tile again doesn't fire it. */
  Marketplace_SubFilter_Selected = "marketplace_subfilter_selected",
  /** The "Filtry" button opened the filter side panel — `section` says which listing it was opened from. */
  Filter_Panel_Opened = "filter_panelOpened",
  MyColectio_NavClick = "myColectio_navClick",
}

enum MyColectioNavTarget {
  Settings = "settings",
  Negotiations = "negotiations",
  Sales = "sales",
  Purchases = "purchases",
}

enum MyColectioNavSource {
  Sidebar = "sidebar",
  Pills = "pills",
  Megamenu = "megamenu",
  UserMenu = "userMenu",
}

export type PostHogObject = {
  capture: (...args: unknown[]) => void;
  identify: (id: string) => void;
};

const trackPageView = (): void => {
  if (isTrackingAllowed()) {
    getPostHogObject()?.capture("$pageview", { $current_url: window.location.href });
  }
};

const trackEvent = async (event: EventName, params?: Record<string, string | number | boolean | null /* feel free to extend types if needed */>): Promise<void> => {
  if (isTrackingAllowed()) {
    getPostHogObject()?.capture(event, params);
  }
};

const identifyUser = async (user: User): Promise<void> => {
  if (isTrackingAllowed()) {
    getPostHogObject()?.identify(user.email);
  }
};

const isTrackingAllowed = (): boolean => {
  const allowedModes = [BuildMode.Production, BuildMode.Beta];
  return allowedModes.includes(buildMode);
};

const getPostHogObject = (): PostHogObject | undefined => {
  if (typeof window.posthog === "undefined") {
    console.warn("PostHog not found.");
    return undefined;
  } else {
    return window.posthog;
  }
};

export default {
  trackPageView,
  trackEvent,
  identifyUser,
  EventName,
  MyColectioNavTarget,
  MyColectioNavSource,
};
